import { API } from '../helpers/Api.helper'
import {
  DriveFolderChildren,
  DriveFolderStats,
  DriveRoot,
  FileCopyDestination,
  FileCopyDryRunResult,
  FileCopyOperation,
  FileCopyOperationFileStatus,
  FileCopyOperationStatus,
  FileCopySelection,
  GetFileCopyOperationResponse,
  ListFileCopyOperationsResponse,
  RetryFileCopyFailuresResponse,
} from '../types/FileCopy'

const BASE = '/api/fileCopy'

export type CreateFileCopyPayload = {
  selection: FileCopySelection
  destination: FileCopyDestination
}

export const FileCopyService = {
  // ── Drive browsing (source) ────────────────────────────────────────────────
  listDriveRoots: (): Promise<DriveRoot[]> =>
    API.get<{ roots: DriveRoot[] }>(`${BASE}/drive/roots`).then(response => response.data.roots),

  listFolderChildren: (folderId: string): Promise<DriveFolderChildren> =>
    API.get<DriveFolderChildren>(`${BASE}/drive/folders/${folderId}/children`).then(
      response => response.data,
    ),

  getFolderStats: (folderId: string): Promise<DriveFolderStats> =>
    API.get<DriveFolderStats>(`${BASE}/drive/folders/${folderId}/stats`).then(
      response => response.data,
    ),

  // ── Operations ─────────────────────────────────────────────────────────────
  dryRun: (payload: CreateFileCopyPayload): Promise<FileCopyDryRunResult> =>
    API.post<CreateFileCopyPayload, FileCopyDryRunResult>(
      `${BASE}/operations/dryRun`,
      payload,
    ).then(response => response.data),

  createOperation: (payload: CreateFileCopyPayload): Promise<FileCopyOperation> =>
    API.post<CreateFileCopyPayload, { operation: FileCopyOperation }>(
      `${BASE}/operations`,
      payload,
    ).then(response => response.data.operation),

  listOperations: (params?: {
    statuses?: FileCopyOperationStatus[]
    page?: number
    pageSize?: number
  }): Promise<ListFileCopyOperationsResponse> =>
    API.get<ListFileCopyOperationsResponse>(`${BASE}/operations`, {
      params: {
        statuses: params?.statuses,
        page: params?.page,
        pageSize: params?.pageSize,
      },
    }).then(response => response.data),

  getOperation: (
    operationId: string,
    params?: {
      fileStatus?: FileCopyOperationFileStatus
      search?: string
      page?: number
      pageSize?: number
    },
  ): Promise<GetFileCopyOperationResponse> =>
    API.get<GetFileCopyOperationResponse>(`${BASE}/operations/${operationId}`, { params }).then(
      response => response.data,
    ),

  retryFailures: (
    operationId: string,
    fileIds?: string[],
  ): Promise<RetryFileCopyFailuresResponse> =>
    API.post<{ fileIds?: string[] }, RetryFileCopyFailuresResponse>(
      `${BASE}/operations/${operationId}/retryFailures`,
      { fileIds },
    ).then(response => response.data),

  cancelOperation: (operationId: string): Promise<FileCopyOperation> =>
    API.post<undefined, { operation: FileCopyOperation }>(
      `${BASE}/operations/${operationId}/cancel`,
      undefined,
    ).then(response => response.data.operation),
}
